import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	CollapseComponent,
	EmptyStateComponent,
	InfoGroupComponent,
} from '@tecalisdev/infinity';
import { MasterService } from '../../../../../shared/services/master.service';
import {
	IOperationBormeDetail,
	IOperationDetailResponse,
	OPERATION_STATUS,
} from '../../../../models/operations.models';
import { notAvailableLabel } from '../../../../../core/utils/utils';
import { OperationDetailOperationMetaComponent } from '../../../../../shared/components/operations/common/operation-detail-operation-meta/operation-detail-operation-meta.component';

type BormeDetailsCollapseSection = 'borme' | 'extra' | 'operation';

@Component({
	selector: 'app-operation-detail-tabs-details-borme',
	imports: [
		TranslatePipe,
		CollapseComponent,
		EmptyStateComponent,
		InfoGroupComponent,
		OperationDetailOperationMetaComponent,
	],
	templateUrl: './operation-detail-tabs-details-borme.component.html',
	styleUrl: './operation-detail-tabs-details-borme.component.scss',
})
export class OperationDetailTabsDetailsBormeComponent implements OnChanges {
	@Input() detail: IOperationDetailResponse;

	private masterService = inject(MasterService);
	private trans = inject(TranslateService);

	public bormeData: IOperationBormeDetail | null = null;
	public detailsRecord: Record<string, unknown> | null = null;
	public OPERATION_STATUS = OPERATION_STATUS;
	public expandedCollapseSection: BormeDetailsCollapseSection | null = 'borme';

	public ngOnChanges(changes: SimpleChanges): void {
		if (!changes['detail']) return;
		this.bormeData = this.detail?.data ? (this.detail.data as unknown as IOperationBormeDetail) : null;
		this.detailsRecord = this.parseDetailsRecord(this.detail?.details);
	}

	private parseDetailsRecord(details: unknown): Record<string, unknown> | null {
		if (details === null || details === undefined) return null;
		if (typeof details === 'object' && !Array.isArray(details)) {
			return details as Record<string, unknown>;
		}
		return null;
	}

	public toggleCollapse(section: BormeDetailsCollapseSection, expanded?: boolean): void {
		this.expandedCollapseSection = !expanded ? null : section;
	}

	public get obtainMethodLabel(): string {
		return this.bormeData?.mode === 'manual' ? 'PROCESSES.DETAIL.MANUAL' : 'PROCESSES.DETAIL.AUTOMATIC';
	}

	public get totalTriesLabel(): string {
		const tries = this.bormeData?.total_tries ?? this.detail?.total_tries;
		if (tries === undefined || tries === null) return notAvailableLabel(this.trans);
		return tries === 0 ? '1' : String(tries);
	}

	public detailFieldLabel(key: string): string {
		return 'OPERATIONS.DETAIL.BORME.DETAILS_FIELD.' + key.toUpperCase();
	}

	/** Etiqueta i18n para una clave de `details` o la clave cruda si no hay traducción. */
	public detailFieldDisplayLabel(key: string): string {
		const k = this.detailFieldLabel(key);
		if (this.masterService.hasTranslation(k)) {
			return this.trans.instant(k);
		}
		return key;
	}

	/** Tipo de operación/conexión (p. ej. SCRIPTURE) con misma resolución que validación financiera. */
	public bormeTypeLabel(): string {
		const type = this.bormeData?.type?.trim();
		if (!type) return notAvailableLabel(this.trans);
		const upper = type.toUpperCase();
		const finKey = 'FINANCIAL_EVALUATION.' + upper;
		if (this.masterService.hasTranslation(finKey)) {
			return this.trans.instant(finKey);
		}
		const connKey = 'MASTER.CONNECTION_TYPE.' + upper;
		if (this.masterService.hasTranslation(connKey)) {
			return this.trans.instant(connKey);
		}
		return type;
	}

	public formatDetailValue(value: unknown): string {
		if (value === null || value === undefined) return notAvailableLabel(this.trans);
		if (typeof value === 'object') {
			try {
				return JSON.stringify(value);
			} catch {
				return notAvailableLabel(this.trans);
			}
		}
		const str = String(value);
		const upper = str.toUpperCase();
		const finKey = 'FINANCIAL_EVALUATION.' + upper;
		if (this.masterService.hasTranslation(finKey)) {
			return this.trans.instant(finKey);
		}
		const connKey = 'MASTER.CONNECTION_TYPE.' + upper;
		if (this.masterService.hasTranslation(connKey)) {
			return this.trans.instant(connKey);
		}
		return str;
	}

	public detailsEntries(): Array<{ key: string; value: unknown }> {
		if (!this.detailsRecord) return [];
		return Object.keys(this.detailsRecord).map((key) => ({ key, value: this.detailsRecord![key] }));
	}

	public showBormeSection(): boolean {
		return !!(
			this.bormeData &&
			(this.bormeData.tax_id ||
				this.bormeData.company_name ||
				this.bormeData.result_summary ||
				this.bormeData.publication_reference ||
				this.bormeData.type)
		);
	}

	public showExtraSection(): boolean {
		return (this.detailsEntries()?.length ?? 0) > 0;
	}
}
